import React, { useRef, forwardRef, useMemo, useImperativeHandle, useEffect } from 'react'
import type { Editor, Extensions, FocusPosition } from '@tiptap/react'
import type { Props as TippyProps } from 'tippy.js'
import { Extension, Node } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import TextStyle from '@tiptap/extension-text-style'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import HardBreak from '@tiptap/extension-hard-break'
import { useEditor, EditorContent } from '@tiptap/react'
import { TextSelection } from 'prosemirror-state'
import type { Context } from '@paintscout/util/templater'
import type { TemplateTag } from '@paintscout/util/util'
import CloseIcon from '@material-ui/icons/Close'

import lodashDebounce from 'lodash/debounce'

import { EditorFloatingMenu } from './EditorFloatingMenu'
import { EditorBubbleMenu } from './EditorBubbleMenu'
import { uuid } from '@paintscout/util/builder'
import striptags from 'striptags'
import type { QuoteFile } from 'paintscout'
import type { DropEvent } from 'react-dropzone'
import { PERMITTED_FILE_TYPES } from '@paintscout/util/s3'
import classNames from 'classnames'
import Dropzone from '@paintscout/ui/components/Dropzone'
import Button from '@paintscout/ui/components/Button'
import Typography from '@paintscout/ui/components/Typography'
import { useTranslation } from '@paintscout/ui'
import InputLabel from '@paintscout/ui/components/InputLabel'
import type { StyleClasses } from '@paintscout/ui/styles'
import { TemplatePlaceholderMark } from './TemplatePlaceholderMark'
import { EmptyPlaceholder } from './EmptyPlaceholder'
import { PersistentSelectionDecoration } from './PersistentSelectionDecoration'
import { useEditorStyles } from './useEditorStyles'
import { TipTapToolbar } from './TipTapToolbar'
import { getPlaceholderRanges, templateToPlaceholderHtml } from './placeholderUtils'
import { isTemplateEditingContext } from './insertTemplatePlaceholder'

// \u200B is the trailing zero-width space (ZERO_WIDTH_SPACE) appended after each
// placeholder span; it's optional so existing templates saved without it still match.
const PLACEHOLDER_WITH_KEY_REGEX =
  /<span[^>]*class="[^"]*template-placeholder[^"]*"[^>]*data-key="([^"]+)"[^>]*>.*?<\/span>\u200B?/g
const PLACEHOLDER_WITH_LABEL_REGEX = /<span[^>]*class="[^"]*template-placeholder[^"]*"[^>]*>([^<]+)<\/span>\u200B?/g

export type EditorMethods = {
  clearContent?: () => void
  getHTML?: () => string
  setHTML?: (value: string) => void
  isDirty?: () => boolean
  focus?: (position?: FocusPosition) => void
}

export type EditorRef = React.MutableRefObject<(Editor & EditorMethods) | null>
export type ForwardedEditorRef = React.ForwardedRef<EditorMethods>

export interface ToolbarConfig {
  bold?: boolean
  italic?: boolean
  underline?: boolean
  color?: boolean
  highlight?: boolean
  headings?: boolean
  lists?: boolean
  align?: boolean
  template?: {
    tooltip?: string
    tags: TemplateTag[]
  }
  link?: boolean
}

export interface EditorProps {
  classes?: StyleClasses<any>
  content: string
  disabled?: boolean
  required?: boolean
  error?: string

  bubbleMenuEnabled?: boolean
  floatingMenuEnabled?: boolean
  disableToolbar?: boolean
  tippyOptions?: Partial<TippyProps>
  /**
   * Renders bare inside surrounding content instead of the boxed toolbar look — currently only the
   * Presentation v2 `HtmlEditor`. Explicit rather than inferred from `bubbleMenuEnabled`/
   * `floatingMenuEnabled`: those two went permanently false when the inline editors' formatting UI
   * moved to the inspector's Format tab (#22203), which would otherwise make every inline editor
   * misread as boxed.
   */
  inline?: boolean

  label?: React.ReactNode
  sublabel?: React.ReactNode
  /** Hint text shown while the editor is empty. Unrelated to toolbar.template's {{placeholders}}. */
  placeholder?: string
  className?: string
  characterCount?: boolean

  fullWidth?: boolean
  singleLine?: boolean
  maxHeight?: number
  /**
   * Text size for the editor's content. `default` inherits the app's editor sizing; `compact` drops it
   * to 14px, for dense surfaces like the Quote Settings dialog. Opt-in, so editors elsewhere are
   * unaffected. It changes the content text only — not the toolbar.
   */
  variant?: 'default' | 'compact'
  templateContext?: Context
  toolbar?: ToolbarConfig

  allowHtml?: boolean

  autofocus?: FocusPosition

  Toolbar?: (props: { editor: Editor }) => JSX.Element
  MenuBar?: (props: { editor: Editor }) => JSX.Element

  onChange?: (value: string) => void
  /**
   * Fires on the underlying TipTap editor's own `onFocus`/`onBlur` — used by presentations v2 to
   * track which text field (of possibly several in one block) currently has caret focus, since a
   * click on the block alone can't disambiguate that (#22203).
   */
  onEditorFocus?: (editor: Editor) => void
  /**
   * The blur `FocusEvent` is forwarded so the consumer can read `relatedTarget` and tell where focus
   * went. Presentations v2 needs that: focusing the inspector's own link-URL input blurs the editor,
   * and clearing the focused field there would unmount the toolbar the user just clicked (#22887).
   */
  onEditorBlur?: (editor: Editor, event?: FocusEvent) => void
  onReset?: () => void
  onAddFiles?: (e?: React.MouseEvent<HTMLDivElement>) => void
  onDrop?: (acceptedFiles: File[], rejectedFiles: File[], event: DropEvent) => void
  onRemoveFile?: (file: QuoteFile) => void
  onEditFile?: (file: QuoteFile) => void
  onSave?: () => void
  saving?: boolean
  onClear?: () => void
  files?: QuoteFile[]
  showFileBar?: boolean
  debounce?: boolean | number
  blockColor?: boolean

  extensions?: Extensions
  /**
   * Overrides the default `getPresentationContentStyles` typography (h1-h4/p, keyed the same way)
   * applied to editor content — Presentations v2 passes its own theme-derived styles so a focused
   * field keeps the presentation's font/color instead of falling back to the ambient app theme
   * (ENG-1438). Omit outside presentations; every other caller is unaffected.
   */
  contentStyles?: Record<string, any>
}

const TipTapEditor = forwardRef((props: EditorProps, ref: React.ForwardedRef<EditorMethods>) => {
  const { __ } = useTranslation()
  const {
    characterCount,
    extensions = [],
    content,
    label,
    sublabel,
    placeholder,
    singleLine,
    disabled,
    blockColor = false,
    debounce: debounceProp = true,
    onChange = () => null,
    onRemoveFile = () => null,
    onSave = null,
    saving = false,
    onEditFile = () => null,
    files = [],
    showFileBar = false,
    required,
    onDrop,
    templateContext,
    toolbar,
    error,
    bubbleMenuEnabled = false,
    floatingMenuEnabled = false,
    disableToolbar = false,
    tippyOptions,
    onEditorFocus,
    onEditorBlur,
    ...otherProps
  } = props

  const debounceTimeout = typeof debounceProp === 'number' ? debounceProp : debounceProp ? 250 : null
  // Unique id to handle lookups w/ multiple editors on same page
  const editorId = useMemo(() => {
    return uuid().split('-')[0]
  }, [])

  const placeholderLabelMap = useMemo(() => {
    return (toolbar?.template?.tags || []).reduce(
      (acc, tag) => {
        acc[tag.key] = tag.label
        return acc
      },
      {} as Record<string, string>
    )
  }, [toolbar?.template?.tags])

  const debouncedOnChange = useMemo(() => {
    return debounceTimeout ? lodashDebounce(onChange, debounceTimeout) : onChange
  }, [])

  const classes = useEditorStyles(props)

  // Check if we're in template editing mode (templateContext values are {{key}} format)
  const isTemplateEditingMode = useMemo(() => isTemplateEditingContext(templateContext), [templateContext])

  const editorExtensions = useMemo(() => {
    return [
      Extension.create({
        name: 'shiftTab',
        addKeyboardShortcuts() {
          return {
            ['Shift-Tab']: () => {
              const { editor } = this

              const { from } = editor.state.selection
              const $from = editor.state.selection.$from

              const lineStartPos = from - $from.parentOffset

              const lineText = editor.state.doc.textBetween(lineStartPos, from, ' ')

              if (lineText.startsWith('\t')) {
                editor.commands.deleteRange({ from: lineStartPos, to: lineStartPos + 1 })
                return true
              }

              return true // prevent default behavior
            }
          }
        }
      }),
      Extension.create({
        name: 'literalTab',
        addKeyboardShortcuts() {
          return {
            Tab: () => {
              const { editor } = this
              if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
                return editor.commands.sinkListItem('listItem')
              } else {
                const { from } = editor.state.selection
                const $from = editor.state.selection.$from

                const lineStartPos = from - $from.parentOffset

                editor.commands.insertContentAt({ from: lineStartPos, to: lineStartPos }, '\t')

                const newPos = from + 1
                editor.commands.setTextSelection(newPos)

                return true
              }
            }
          }
        }
      }),
      TextStyle,
      StarterKit.configure({
        hardBreak: false
      }),
      HardBreak.extend({
        addKeyboardShortcuts() {
          return {
            Enter: () => {
              return this.editor.commands.newlineInCode()
            },
            ['Shift-Enter']: () => {
              return this.editor.commands.enter()
            },
            ['Mod-Enter']: () => {
              return this.editor.commands.enter()
            }
          }
        }
      }),
      TextAlign.configure({
        alignments: ['left', 'center', 'right', 'justify'],
        types: ['heading', 'paragraph'],
        defaultAlignment: 'left'
      }),
      Link.configure({
        openOnClick: false
      }),
      // CharacterCount.configure({
      //   limit: characterCount || null
      // }),
      TemplatePlaceholderMark,
      PersistentSelectionDecoration,
      EmptyPlaceholder.configure({ placeholder }),
      ...(blockColor
        ? []
        : [
            Color,
            Highlight.configure({
              multicolor: true
            })
          ]),
      Underline,
      ...extensions
    ]
  }, [extensions, placeholder]) as any as Extensions

  const SingleLiner = Node.create({
    name: 'singleLiner',
    topNode: true,
    content: 'block'
  })

  if (singleLine) {
    editorExtensions.unshift(SingleLiner)
  }

  // Reverse function to convert formatted label back to camelCase key
  // e.g., "Customer Name" -> "customerName", "Customer First Name" -> "customerFirstName", "I D" -> "ID"
  const reversePlaceholderLabel = (formatted: string): string => {
    if (!formatted) return formatted
    const words = formatted.trim().split(/\s+/)
    if (words.length === 0) return formatted

    // Handle all-caps single-letter words (e.g., "I D" -> "ID")
    // If all words are single uppercase letters, combine them without lowercasing
    if (words.every((word) => word.length === 1 && word === word.toUpperCase())) {
      return words.join('')
    }

    // Lowercase first word completely, then capitalize first letter of each subsequent word and join
    const firstWord = words[0].toLowerCase()
    const restWords = words.slice(1).map((word) => {
      const lowercased = word.toLowerCase()
      return lowercased.charAt(0).toUpperCase() + lowercased.slice(1)
    })
    return firstWord + restWords.join('')
  }

  // Convert {{key}} to placeholder spans for display in template editing mode
  const processedContent = useMemo(() => {
    if (!content || !isTemplateEditingMode || typeof content !== 'string') return content
    return templateToPlaceholderHtml(content, placeholderLabelMap)
  }, [content, isTemplateEditingMode, placeholderLabelMap])

  const editor = useEditor({
    extensions: editorExtensions,
    content: processedContent,
    editable: !disabled,
    editorProps: {
      handleScrollToSelection: (_view) => {
        return true
      },
      handleKeyDown: (view, event) => {
        const { state } = view
        const { selection, doc } = state
        if (!selection.empty) return false

        const { $from } = selection
        const pos = $from.pos

        const ranges = getPlaceholderRanges(doc)
        if (!ranges.length) return false

        const findRangeAt = (position: number) => ranges.find((r) => position >= r.start && position < r.end)
        const findNextRange = (position: number) => ranges.find((r) => r.start >= position)
        const findPrevRange = (position: number) => {
          for (let i = ranges.length - 1; i >= 0; i -= 1) {
            if (ranges[i].end <= position) return ranges[i]
          }
          return null
        }

        const currentRange = findRangeAt(pos)

        if (event.key === 'Backspace') {
          const targetRange = ranges.find((range) => range.end === pos)
          if (targetRange) {
            const tr = state.tr.deleteRange(targetRange.start, targetRange.end)
            tr.setSelection(TextSelection.create(tr.doc, targetRange.start))
            view.dispatch(tr)
            return true
          }
        }

        if (event.key === 'Delete') {
          const targetRange = ranges.find((range) => range.start === pos)
          if (targetRange) {
            const tr = state.tr.deleteRange(targetRange.start, targetRange.end)
            tr.setSelection(TextSelection.create(tr.doc, targetRange.start))
            view.dispatch(tr)
            return true
          }
        }

        if (event.key === 'Tab') {
          const targetRange = event.shiftKey ? currentRange || findPrevRange(pos) : currentRange || findNextRange(pos)
          if (targetRange) {
            const target = event.shiftKey ? targetRange.start : targetRange.end
            view.dispatch(state.tr.setSelection(TextSelection.create(doc, target)))
            return true
          }
          return false
        }

        if (event.key === 'ArrowLeft') {
          const targetPos = Math.max(pos - 1, 0)
          const targetRange = findRangeAt(pos) || findRangeAt(targetPos)
          if (targetRange) {
            const goal =
              pos === targetRange.start && targetRange.start > 0
                ? Math.max(targetRange.start - 1, 0)
                : targetRange.start
            view.dispatch(state.tr.setSelection(TextSelection.create(doc, goal)))
            return true
          }
        }

        if (event.key === 'ArrowRight') {
          const targetPos = Math.min(pos + 1, doc.content.size)
          const targetRange = findRangeAt(pos) || findRangeAt(targetPos)
          if (targetRange) {
            view.dispatch(state.tr.setSelection(TextSelection.create(doc, targetRange.end)))
            return true
          }
        }

        return false
      },
      transformPastedHTML(html) {
        // Strip out header tags when pasting
        return striptags(html, [
          'b',
          'u',
          'i',
          'ins',
          'em',
          'br',
          'strong',
          'del',
          'p',
          'ul',
          'ol',
          'li',
          'img',
          'div',
          'span',
          'mark',
          'a'
        ])
      }
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      const convert = (h: string) =>
        h
          .replace(
            /<span[^>]*class="[^"]*template-placeholder[^"]*"[^>]*data-key="([^"]+)"[^>]*>.*?<\/span>/g,
            (_, k) => `{{${k}}}`
          )
          .replace(
            /<span[^>]*class="[^"]*template-placeholder[^"]*"[^>]*>([^<]+)<\/span>/g,
            (_, t) => `{{${reversePlaceholderLabel(t.trim())}}}`
          )
      debouncedOnChange?.(isTemplateEditingMode ? convert(html) : html)
    },
    onFocus: ({ editor }) => onEditorFocus?.(editor),
    onBlur: ({ editor, event }) => onEditorBlur?.(editor, event),
    parseOptions: {
      preserveWhitespace: true
    }
  })

  // Disabled sync
  useEffect(() => {
    if (editor) {
      editor.setOptions({
        editable: !disabled
      })
    }
  }, [disabled])

  // External content sync: when the `content` prop changes from the outside
  // (e.g. a list of editors is reordered and this instance is reused at a new
  // position via index keys), reflect it in the editor. Without this the editor
  // is effectively uncontrolled after mount, so reordering documents leaves the
  // description "behind". Mirrors the guards in setHTML so we never clobber an
  // active edit or fight iOS Safari's keyboard/caret.
  useEffect(() => {
    if (!editor) return
    const target = processedContent ?? ''
    if (editor.getHTML() === target) return
    if (editor.isFocused) return
    if (typeof document !== 'undefined') {
      const dom = editor.view?.dom as HTMLElement | undefined
      const active = document.activeElement
      if (dom && active && (dom === active || dom.contains(active))) return
    }
    // Match the initial parse (`parseOptions.preserveWhitespace` on useEditor).
    // Without this, setContent re-parses with the default (collapse whitespace),
    // which silently flattens runs of spaces used for column-aligned content
    // (e.g. pasted "table chart" descriptions), collapsing the formatting.
    editor.commands.setContent(target, false, { preserveWhitespace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedContent])

  const toTemplate = (html: string) =>
    html
      .replace(PLACEHOLDER_WITH_KEY_REGEX, (_, k) => `{{${k}}}`)
      .replace(PLACEHOLDER_WITH_LABEL_REGEX, (_, t) => `{{${reversePlaceholderLabel(t.trim())}}}`)
  const toPlaceholder = (html: string) => templateToPlaceholderHtml(html, placeholderLabelMap)

  const editorRef: EditorRef = useRef(null)
  useImperativeHandle(ref, () => ({
    clearContent: () => {
      editorRef.current?.commands?.clearContent()
    },
    getHTML: () => {
      const html = editorRef.current?.getHTML() || ''
      return isTemplateEditingMode ? toTemplate(html) : html
    },
    setHTML: (value: string) => {
      const editor = editorRef.current
      if (!editor) return
      const target = isTemplateEditingMode ? toPlaceholder(value) : value
      // Skip the setContent transaction when nothing would change. On iOS
      // Safari a redundant DOM rebuild during the user's tap gesture causes
      // the keyboard to dismiss and the caret to disappear.
      if (editor.getHTML() === target) return
      // If the editor currently has focus, the user is actively editing —
      // defer external content updates. Replacing content under an active tap
      // on iOS Safari dismisses the keyboard, and overwriting the user's
      // in-progress edits from a background state sync is hostile regardless
      // of platform.
      if (editor.isFocused) return
      // Belt-and-suspenders for iOS Safari: between touchend and the synthetic
      // focus event there's a window where editor.isFocused is false but the
      // contenteditable already is (or contains) document.activeElement. A
      // setContent transaction here cancels the pending tap-to-focus.
      if (typeof document !== 'undefined') {
        const dom = editor.view?.dom as HTMLElement | undefined
        const active = document.activeElement
        if (dom && active && (dom === active || dom.contains(active))) return
      }
      // Preserve whitespace to match the initial parse (see useEditor parseOptions).
      editor.commands?.setContent(target, false, { preserveWhitespace: true })
    },
    isDirty: () => {
      const html = editorRef.current?.getHTML() || ''
      return props.content !== (isTemplateEditingMode ? toTemplate(html) : html)
    },
    focus: (position?: FocusPosition) => {
      editorRef.current?.chain().focus(position).scrollIntoView().run()
    }
  }))
  const ToolbarComponent = disableToolbar ? null : props.Toolbar ?? TipTapToolbar
  if (!editor) return null
  editorRef.current = editor
  const showFileFunctions = showFileBar && (files.length > 0 || (onSave && !editorRef.current.isEmpty))

  return (
    <>
      <div>
        {label ? (
          <InputLabel disabled={disabled} required={required} className={classes.label}>
            {label}
          </InputLabel>
        ) : null}
      </div>

      {sublabel ? (
        <div style={{ padding: '0px 0px 8px 0px' }}>
          <Typography variant="subtitle2">{sublabel}</Typography>
        </div>
      ) : null}

      <Dropzone
        onDrop={onDrop}
        accept={PERMITTED_FILE_TYPES.join(',')}
        buttonFullHeight={false}
        shouldRenderPreview
        noClick
        renderPreview={({ isDragActive }) => {
          return (
            <>
              <div
                className={classNames({
                  [classes.root]: true,
                  [classes.dropRoot]: isDragActive,
                  [classes.withFileBar]: showFileFunctions
                })}
                {...otherProps}
              >
                {ToolbarComponent && editor && <ToolbarComponent editor={editorRef.current} {...props} />}
                {floatingMenuEnabled && editor && (
                  <EditorFloatingMenu
                    editor={editorRef.current}
                    noDisplayClassName={classes.noDisplay}
                    toolbar={toolbar}
                    templateContext={templateContext}
                    tippyOptions={tippyOptions}
                  />
                )}
                {bubbleMenuEnabled && editor && (
                  <EditorBubbleMenu
                    editor={editorRef.current}
                    noDisplayClassName={classes.noDisplay}
                    toolbar={toolbar}
                    templateContext={templateContext}
                    blockColor={blockColor}
                    tippyOptions={tippyOptions}
                  />
                )}
                <EditorContent
                  editor={editorRef.current}
                  className={classes.editorContent}
                  id={`EditorId-${editorId}`}
                />
                {showFileFunctions && (
                  <div className={classes.fileDivContainer}>
                    <div id="files-container" className={classes.filesContainer}>
                      {files.map((file, index) => {
                        const fileExtension = file.type.includes('/') ? file.type.split('/')[1] : file.type
                        // const unnamedIndex = files.slice(0, index + 1).filter((f) => !f.title).length
                        const displayName = file.title
                          ? file.title.includes(fileExtension)
                            ? file.title
                            : `${file.title}.${fileExtension}`
                          : `Untitled file`
                        return (
                          <div key={index} className={classes.fileItem}>
                            <div
                              className={classes.fileItemTextDiv}
                              onClick={() => (disabled ? null : onEditFile(file))}
                            >
                              <Typography variant="body1" className={classes.fileItemText}>
                                {displayName}
                              </Typography>
                            </div>
                            <CloseIcon
                              onClick={() => (disabled ? null : onRemoveFile(file))}
                              className={classes.fileCloseIcon}
                            />
                          </div>
                        )
                      })}
                    </div>
                    {onSave && (
                      <Button
                        variant="hoverUnderline"
                        color="primary"
                        onClick={() => onSave()}
                        className={classes.saveButton}
                        disabled={disabled}
                        loading={saving}
                      >
                        {__('Save')}
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {characterCount && (
                <div className={classes.characterCountDiv}>
                  <Typography variant="subtitle2">
                    {`Characters: ${editorRef.current ? editorRef.current.getText().length : 0}`}
                  </Typography>
                </div>
              )}
              {error && (
                <div className={classes.errorDiv}>
                  <Typography variant="subtitle2" color="error">
                    {error}
                  </Typography>
                </div>
              )}
            </>
          )
        }}
        {...props}
      />
    </>
  )
})
TipTapEditor.displayName = 'TipTapEditor'

export { TipTapEditor as Editor }
export type { Editor as TipTapEditor }
